import { BreadcrumbItem } from './models/breadcrumb-item.model';
import { BreadcrumbItemService } from './breadcrumb-item-service';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { filter, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslationService } from 'app/core/internationalization/translation.service';
@UntilDestroy()
@Component({
    selector: 'kh-breadcrumbs',
    styleUrls: ['./breadcrumbs.component.less'],
    templateUrl: './breadcrumbs.component.html',
    standalone: false
})
export class BreadcrumbsComponent implements OnInit {

  public splitChar = '.';
  public data$: Observable<BreadcrumbItem | null> | undefined = undefined;
  public currentPath = '';
  public currentLevel = 1;
  public placeholderLevel2 = '';
  public placeholderLevel3 = '';
  public strandSelected: BreadcrumbItem | undefined = undefined;
  public topicSelected: BreadcrumbItem | undefined = undefined;

  public flatList: BreadcrumbItem[] = [];

  @Output() public pathSelected = new EventEmitter<string>();

  public constructor(private readonly _translationService: TranslationService,
    private readonly breadcrumbItemService: BreadcrumbItemService ) { }

  public ngOnInit(): void {

    this.data$ = this.breadcrumbItemService.root$
      .pipe(
        filter(root => root !== null),
        tap( root => this.setFlatList(root as BreadcrumbItem))
      );

    this.breadcrumbItemService.path$
      .pipe(
        tap( path => {
          this.currentPath = path;
          const split = this.currentPath.split(this.splitChar);
          this.currentLevel = split.length;
          this.expandSelected(this.currentLevel);

          if (this.currentLevel === 1) {
            // un-select it
            this.strandSelected = undefined;
          }

          if (this.currentLevel === 2 || (this.currentLevel === 3 && this.strandSelected === undefined)) {
            const lastLevelId = split[1];
            this.strandSelected = this.flatList.find( x => x.id === lastLevelId && x.parent?.id === split[0]);
          }

          if (this.currentLevel === 3) {
            const lastLevelId = split[2];
            this.topicSelected = this.flatList.find( x => x.id === lastLevelId && x.parent?.id === split[1]);
          }
        }),
        untilDestroyed(this)
      )
      .subscribe()

    this.placeholderLevel2 = this._translationService.instant('BREADCRUMBS.SELECT_AN_ITEM');
    this.placeholderLevel3 = this._translationService.instant('BREADCRUMBS.SELECT_AN_ITEM');
  }

  public getPath(item: BreadcrumbItem): string {
    if (!item) {
      return '';
    }

    if (item.parent) {
      return `${this.getPath(item.parent)}.${item.id}`;
    }

    return item.id;
  }

  public selectPath(item: BreadcrumbItem):void {
    this.currentPath = this.getPath(item);
    this.currentLevel = this.currentPath.split(this.splitChar).length;
    this.expandSelected(this.currentLevel);
    this.pathSelected.emit(this.currentPath);
  }

  public getName(item: BreadcrumbItem): string {

    return item.label
  }

  public expand(item:BreadcrumbItem): void {
    this.flatList.forEach(x => x.isExpanded = false);
    item.isExpanded = true;
  }

  public currentHasChildren(level: number, parent: BreadcrumbItem): boolean {
    let hasChild = false;
    if (this.currentLevel == level && parent.children.length) {
      const childId = this.currentPath.split(this.splitChar)[level-1];
      parent.children.forEach(c => {
        if (c.id === childId && c.children.length) {
          hasChild = true;
        }
      })
    }

    return hasChild;
  }

  public expandSelected(level?: number): void {
    let safeLevel = level;
    if (safeLevel !== undefined) {
      safeLevel = this.currentLevel;
    }
    const found = this.getSelected(safeLevel);
    this.flatList.forEach(x => x.isExpanded = false);
    if (found) {
      this.expandAndParent(found);
    }
  }

  private expandAndParent(item: BreadcrumbItem):void {
    if (item.children.length) {
      item.isExpanded = true;
    }
    if (item.parent) {
      this.expandAndParent(item.parent);
    }
  }

  public getSelected(level = 1): BreadcrumbItem {
    let pathAtLevel = '';
    const split = this.currentPath.split(this.splitChar);
    for (let i = 0; i < level; i++) {
      if (pathAtLevel === '') {
        pathAtLevel+=split[i];
      } else {
        pathAtLevel+= this.splitChar+split[i];
      }

    }

    const found = this.flatList.find(c => this.getPath(c) === pathAtLevel);

    return found as BreadcrumbItem;
  }

  public setFlatList(item: BreadcrumbItem): BreadcrumbItem[] {
    this.flatList = [];
    this.addWithChildrenToFlatList(item as BreadcrumbItem);

    return this.flatList;
  }

  public addWithChildrenToFlatList(item: BreadcrumbItem): void {
    this.addToFlatList(item);
    if(item.children.length) {
      item.children.forEach(c => this.addWithChildrenToFlatList(c));
    }
  }

  private addToFlatList(item: BreadcrumbItem): void {
    if (!this.flatList.find(x => x.id == item.id)) {
      this.flatList.push(item);
    }
  }
}
